import { createBrowserClient, type CookieOptions } from '@supabase/ssr';
import { isInternalPath } from '@/lib/safePath';

const PFX = 'sb_';

const canUseCookies = (() => {
  let cache: boolean | null = null;
  return () => {
    if (typeof document === 'undefined') return false;
    if (cache !== null) return cache;
    const k = '__sb_test__';
    document.cookie = `${k}=1; Path=/; SameSite=None; Secure; Partitioned`;
    cache = document.cookie.includes(k);
    document.cookie = `${k}=; Path=/; Max-Age=0; SameSite=None; Secure`;
    return cache;
  };
})();

const fromCookies = () =>
  typeof document === 'undefined'
    ? []
    : document.cookie
        .split(';')
        .filter(Boolean)
        .map((c) => {
          const eqIdx = c.trim().indexOf('=');
          const name = eqIdx >= 0 ? c.trim().slice(0, eqIdx) : c.trim();
          const value = eqIdx >= 0 ? decodeURIComponent(c.trim().slice(eqIdx + 1)) : '';
          return { name: name.trim(), value };
        })
        .filter((c) => c.name);

const fromStorage = () => {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX))
      .map((k) => ({ name: k.slice(PFX.length), value: localStorage.getItem(k) || '' }));
  } catch {
    return [];
  }
};

const setCookie = (name: string, value: string, options?: any) => {
  let s = `${name}=${encodeURIComponent(value)}; Path=${options?.path || '/'}; SameSite=None; Secure; Partitioned`;
  if (options?.maxAge) s += `; Max-Age=${options.maxAge}`;
  if (options?.domain) s += `; Domain=${options.domain}`;
  if (options?.expires) s += `; Expires=${new Date(options.expires).toUTCString()}`;
  document.cookie = s;
};
const deleteCookie = (name: string) => {
  if (typeof document === 'undefined') return;

  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const domains = ['', host, host ? `.${host}` : ''].filter(Boolean);

  const variants = [
    'Path=/; SameSite=Lax',
    'Path=/; SameSite=None; Secure',
    'Path=/; SameSite=None; Secure; Partitioned',
  ];

  variants.forEach((attrs) => {
    document.cookie = `${name}=; Max-Age=0; ${attrs}`;
    domains.forEach((domain) => {
      document.cookie = `${name}=; Max-Age=0; Domain=${domain}; ${attrs}`;
    });
  });
};

/**
 * The session token, reassembled if the browser split it.
 *
 * Supabase chunks a session that exceeds the 4KB cookie limit into
 * `…auth-token.0`, `…auth-token.1` and so on. Taking the first match returned
 * half a token, which the middleware then injected as if it were whole — the
 * server could not parse it, concluded nobody was signed in, and bounced a
 * perfectly valid login back to the login screen.
 *
 * Sorted numerically by suffix rather than relying on storage order, because
 * neither `document.cookie` nor `Object.keys(localStorage)` promises one, and
 * chunks joined out of order fail exactly as silently as a truncated one.
 */
const getToken = () => {
  const all = (canUseCookies() ? fromCookies() : fromStorage()).filter((c) =>
    c.name.includes('auth-token')
  );
  if (all.length === 0) return null;

  const chunks = all
    .map((c) => ({ ...c, index: Number(c.name.match(/\.(\d+)$/)?.[1] ?? -1) }))
    .filter((c) => c.index >= 0)
    .sort((a, b) => a.index - b.index);

  if (chunks.length > 0) return chunks.map((c) => c.value).join('');
  return all[0].value;
};

if (typeof window !== 'undefined' && !(window as any).__sb_patched__) {
  (window as any).__sb_patched__ = true;
  const orig = window.fetch.bind(window);
  window.fetch = (input, init) => {
    const token = getToken();
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.href
          : (input as Request).url;
    // `startsWith('/')` also matched `//other-origin`, which would have sent
    // the session token somewhere else entirely.
    if (token && (isInternalPath(url) || url.startsWith(window.location.origin))) {
      init = { ...(init || {}), headers: { ...(init?.headers || {}), 'x-sb-token': token } };
    }
    return orig(input, init);
  };
}

function buildClient() {
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookies: {
        getAll: () => (canUseCookies() ? fromCookies() : fromStorage()),
        setAll(cookiesToSet: Array<{ name: string; value: string; options?: CookieOptions }>) {
          if (typeof document === 'undefined') return;
          if (canUseCookies()) {
            cookiesToSet.forEach(({ name, value, options }) =>
              value ? setCookie(name, value, options) : deleteCookie(name)
            );
          } else {
            cookiesToSet.forEach(({ name, value, options }) => {
              try {
                value
                  ? localStorage.setItem(`${PFX}${name}`, value)
                  : localStorage.removeItem(`${PFX}${name}`);
              } catch {}
              if (value) setCookie(name, value, options);
            });
          }
        },
      },
    }
  );
}

let _client: ReturnType<typeof buildClient> | null = null;

/**
 * The one browser client. There must never be a second.
 *
 * Every `createBrowserClient` call builds a fresh GoTrueClient, and each of
 * those installs its own token-refresh timer and its own `visibilitychange`
 * listener. Eighteen call sites meant eighteen of them: returning to the tab
 * woke every one at once, each emitting its own auth event, each event landing
 * as a React state update — which is what made the app appear to reload itself
 * and throw away whatever was half-typed.
 *
 * So `createClient` is not a constructor. It is this, memoised, and the name
 * is kept only because the call sites read naturally with it.
 */
export function createClient() {
  if (!_client) {
    _client = buildClient();
  }
  return _client;
}

export function getClient() {
  if (typeof window === 'undefined') return null;
  if (!process.env.NEXT_PUBLIC_SUPABASE_URL || !process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY) {
    return null;
  }
  return createClient();
}

export function clearStaleAuth() {
  try {
    // Clear all supabase-related localStorage keys
    Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX) || k.startsWith('sb-'))
      .forEach((k) => localStorage.removeItem(k));
  } catch {}
  // Clear supabase cookies
  if (typeof document !== 'undefined') {
    document.cookie.split(';').forEach((c) => {
      const name = c.trim().split('=')[0];
      if (name.startsWith('sb-') || name.includes('supabase')) {
        deleteCookie(name);
      }
    });
  }
}

export function isStaleSessionError(error: unknown): boolean {
  if (!error) return false;
  const msg = error instanceof Error ? error.message : String(error);
  return (
    msg.includes('refresh_token_not_found') ||
    msg.includes('Invalid Refresh Token') ||
    msg.includes('JWT expired') ||
    msg.includes('session_not_found')
  );
}
